import { Grid, Tooltip } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";
import InfoIcon from "@mui/icons-material/Info";
import {
    ModelComputeFrequency,
    ModelComputeOptionEnabled,
} from "models/ModelCompute";
import ServiceHierarchySelect from "../OrganizationModules/fields/ServiceHierarchySelect";
import AcxSwitch from "components/UI/AcxSwitch";
import ControlsContainer from "./ControlsContainer";

const freqOptions = [
    {
        label: "Day",
        value: 1,
    },
    {
        label: "Week",
        value: 2,
    },
];

type Props = {
    SEEnabled: boolean;
    setIsAll: (isAll: boolean) => void;
    isAll: boolean;
    orgModelComputeOptions: ModelComputeOptionEnabled[] | null | undefined;
    orgId: string;
    handleUpdateSEModelOSMIds: (ids: string[] | null) => void;
    handleUpdateSEModelQuota: (quota?: number) => void;
    SEConvoNum?: number;
    SEDefaultFreq?: ModelComputeFrequency;
    handleUpdateSEModelFreq: (freq?: ModelComputeFrequency) => void;
    handleModelOptionToggle: (
        checked: boolean,
        modelOptionName: string,
    ) => void;
    isViewOnly: boolean;
};

const SafetyEventModelControls: React.FC<Props> = observer((props: Props) => {
    const heading = "Safety Events";
    const description = `A family of models that flags Safety Events (product quality
                complaints, adverse events, and special situations), along with
                identifying where in the interaction the Safety Event was
                flagged.`;

    return (
        <>
            <ControlsContainer
                isViewOnly={props.isViewOnly}
                heading={heading}
                description={description}
                isEnabled={props.SEEnabled}
                setIsEnabled={(checked) => {
                    props.handleModelOptionToggle(checked, "SafetyEvents");
                }}
                maxHeight={350}
            >
                <Grid
                    container
                    item
                    xs={12}
                    style={{
                        padding: theme.spacing(1),
                    }}
                    alignItems="center"
                >
                    <Grid container item xs={12} alignItems={"center"}>
                        <AcxSwitch
                            onChange={(_, checked) => {
                                props.setIsAll(checked);
                            }}
                            checked={props.isAll}
                            disabled={props.isViewOnly}
                        />
                        Run the Safety Event Model on all conversations
                    </Grid>
                    {!props.isAll && (
                        <>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    fontWeight: 400,
                                    color: theme.palette.gray[500],
                                }}
                            >
                                - or -
                            </Grid>
                            <Grid item xs={12}>
                                <ServiceHierarchySelect
                                    maxContainerHeight="200px"
                                    orgId={props.orgId!}
                                    value={
                                        props.orgModelComputeOptions
                                            ?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "SafetyEvents",
                                            )
                                            ?.modelComputeOption.modelComputeStructureMembers?.map(
                                                (i) =>
                                                    i.organizationStructureMemberId,
                                            ) ?? []
                                    }
                                    onChange={props.handleUpdateSEModelOSMIds}
                                    fullWidth
                                    label=" "
                                    placeholder="Select Hierarchies to run SE model on"
                                    isDisabled={props.isViewOnly}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={3}
                                style={{
                                    paddingTop: theme.spacing(1),
                                }}
                                alignItems={"center"}
                                container
                            >
                                Run the model on up to{" "}
                                <Tooltip title="Model will be run within 5% of defined volume">
                                    <InfoIcon
                                        color="primary"
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid
                                container
                                item
                                xs={9}
                                alignItems="center"
                                style={{
                                    paddingTop: theme.spacing(1),
                                }}
                            >
                                <Grid item xs={3}>
                                    <AcxMainTextField
                                        id="num-conversations-model-control-input"
                                        placeholderText="Enter # of conversations..."
                                        onChange={(e) => {
                                            props.handleUpdateSEModelQuota(
                                                Number(e.target.value),
                                            );
                                        }}
                                        type="number"
                                        insertBlankIfNumber
                                        value={props.SEConvoNum}
                                        isDisabled={
                                            props.isAll || props.isViewOnly
                                        }
                                        error={(props.SEConvoNum ?? 1) <= 0}
                                        errorHelper="Value must be greater than 0"
                                    />
                                </Grid>
                                <Grid
                                    container
                                    item
                                    xs={4}
                                    justifyContent="center"
                                    sx={{
                                        fontWeight: 400,
                                        color: theme.palette.gray[700],
                                    }}
                                >
                                    conversations per
                                </Grid>
                                <Grid item xs={4}>
                                    <AcxSelect
                                        id="time-period-select-model-control"
                                        options={freqOptions}
                                        labelField="label"
                                        valueField="value"
                                        placeholder="Select Time"
                                        onChange={(i) => {
                                            props.handleUpdateSEModelFreq(
                                                i.value ?? 0,
                                            );
                                        }}
                                        defaultValue={freqOptions.find(
                                            (i) =>
                                                i.value === props.SEDefaultFreq,
                                        )}
                                        isDisabled={
                                            props.isAll || props.isViewOnly
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </ControlsContainer>
        </>
    );
});

export default SafetyEventModelControls;
